import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db } from '../firebase-config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import AffiliateAuthModal from './AffiliateAuthModal';

export default function UserAuthMenu({ compact = false }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // Fetch user role from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || 'user');
            console.log('User role from Firestore:', userData.role);
          } else {
            // Try affiliates collection
            const affiliateDoc = await getDoc(doc(db, 'affiliates', currentUser.uid));
            if (affiliateDoc.exists()) {
              const affiliateData = affiliateDoc.data();
              setUserRole(affiliateData.role || 'affiliate');
              console.log('User role from affiliates:', affiliateData.role);
            } else {
              setUserRole('user');
            }
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          setUserRole('user');
        }
      } else {
        setUserRole(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogin = () => {
    console.log('Login button clicked, opening modal');
    setShowLoginModal(true);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsDropdownOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDashboard = () => {
    if (userRole === 'super_admin') {
      navigate('/admin/super');
    } else if (userRole === 'admin') {
      navigate('/admin/dashboard');
    } else {
      navigate('/affiliate/dashboard');
    }
    setIsDropdownOpen(false);
  };

  const handleProfile = () => {
    navigate('/affiliate/dashboard');
    setIsDropdownOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center">
        <div className="w-8 h-8 border-2 border-white/[0.08] border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    // Not logged in - show login button
    return (
      <div>
        <button
          onClick={handleLogin}
          className={compact
            ? "w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-[#9ca3af] hover:text-white hover:bg-white/[0.06] transition-all"
            : "px-2 py-1.5 bg-secondary hover:bg-hover text-white font-medium text-[11px] rounded-md transition-all duration-200 whitespace-nowrap"
          }
        >
          {compact ? (
            <>
              <span className="w-6 h-6 rounded-full bg-[#e31937]/20 border border-[#e31937]/30 flex items-center justify-center text-[#e31937] text-xs flex-shrink-0">?</span>
              <span className="truncate">Belépés</span>
            </>
          ) : 'Belépés'}
        </button>
        
        {showLoginModal && (
          <AffiliateAuthModal 
            onClose={() => {
              console.log('Closing modal');
              setShowLoginModal(false);
            }} 
            initialMode="login" 
          />
        )}
      </div>
    );
  }

  // Logged in - show user menu
  const displayName = user.displayName || user.email?.split('@')[0] || 'User';
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const isSuperAdmin = userRole === 'super_admin';

  if (compact) {
    return (
      <div className="relative" ref={dropdownRef} style={{ zIndex: 9999 }}>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsDropdownOpen(p => !p); }}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-[#9ca3af] hover:text-white hover:bg-white/[0.06] transition-all"
        >
          <div className="w-6 h-6 bg-gradient-to-br from-[#e31937] to-[#b91c1c] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {displayName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-medium truncate">{displayName}</p>
            <p className="text-[#6b7280] text-[10px] truncate">{isSuperAdmin ? 'Super Admin' : isAdmin ? 'Admin' : 'Felhasználó'}</p>
          </div>
        </button>
        {isDropdownOpen && (
          <div className="absolute bottom-full left-0 mb-2 w-56 bg-[#1c1c1c] border border-white/[0.08] rounded-xl shadow-2xl" style={{ zIndex: 999999 }} onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b border-white/[0.06]">
              <p className="text-white text-xs font-semibold truncate">{displayName}</p>
              <p className="text-[#6b7280] text-[10px] truncate">{user.email}</p>
            </div>
            <div className="p-1.5 space-y-0.5">
              {isSuperAdmin && <Link to="/admin/super" onClick={() => setIsDropdownOpen(false)} className="flex items-center gap-2 px-3 py-2 text-white hover:bg-white/[0.06] rounded-lg text-xs transition-colors"><span className="text-[#e31937]">⚡</span> Super Admin</Link>}
              <Link to="/affiliate/dashboard" onClick={() => setIsDropdownOpen(false)} className="flex items-center gap-2 px-3 py-2 text-white hover:bg-white/[0.06] rounded-lg text-xs transition-colors"><span>👤</span> Profil</Link>
              <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg text-xs transition-colors"><span>↩</span> Kijelentkezés</button>
            </div>
          </div>
        )}
        {showLoginModal && <AffiliateAuthModal onClose={() => setShowLoginModal(false)} initialMode="login" />}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef} style={{ zIndex: 9999 }}>
      <button
        onClick={(e) => {
          console.log('Button clicked, current state:', isDropdownOpen);
          e.preventDefault();
          e.stopPropagation();
          setIsDropdownOpen(prev => {
            console.log('Setting dropdown to:', !prev);
            return !prev;
          });
        }}
        className="relative flex items-center justify-center px-1.5 py-1 mx-1 rounded-lg font-medium text-white text-xs
          bg-gradient-to-b from-[#23232b]/70 to-[#101015]/90
          shadow-md
          border border-white/10
          backdrop-blur-md
          transition-all duration-200
          hover:bg-gradient-to-b hover:from-[#23232b]/90 hover:to-[#23232b]/70
          hover:shadow-lg hover:scale-105
          active:scale-95
          focus:outline-none focus:ring-2 focus:ring-[#e31937]/60
          whitespace-nowrap
          max-w-[160px]"
        style={{ zIndex: 9998 }}
      >
        <div className="flex items-center space-x-0.5 overflow-hidden">
          <div className="w-5 h-5 bg-gradient-to-br from-[#e31937] to-[#b91c1c] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {displayName.charAt(0).toUpperCase()}
          </div>
          <span className="hidden xl:block max-w-12 truncate text-xs flex-shrink-0 ml-1">{displayName}</span>
          {isSuperAdmin && (
            <span className="bg-[#e31937] text-white text-xs px-1 py-0.5 rounded-full flex-shrink-0 ml-0.5">
              SA
            </span>
          )}
          {isAdmin && !isSuperAdmin && (
            <span className="bg-[#e31937] text-white text-xs px-1 py-0.5 rounded-full flex-shrink-0 ml-0.5">
              A
            </span>
          )}
        </div>
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div 
          className="absolute right-0 top-full mt-2 w-64 bg-[#1a1a1a] rounded-xl shadow-2xl border border-white/10 backdrop-blur-md" 
          style={{zIndex: 999999}}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#e31937] to-[#b91c1c] rounded-full flex items-center justify-center text-white text-lg font-bold">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-white font-semibold">{displayName}</p>
                <p className="text-gray-400 text-sm">{user.email}</p>
                {isSuperAdmin && (
                  <span className="inline-block bg-[#e31937] text-white text-xs px-2 py-1 rounded-full mt-1">
                    Super Admin
                  </span>
                )}
                {isAdmin && !isSuperAdmin && (
                  <span className="inline-block bg-[#e31937] text-white text-xs px-2 py-1 rounded-full mt-1">
                    Admin
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="p-2">
            {isSuperAdmin && (
              <Link
                to="/admin/super"
                onClick={() => setIsDropdownOpen(false)}
                className="flex items-center px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 mr-3 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Super Admin Dashboard
              </Link>
            )}
            
            {isAdmin && !isSuperAdmin && (
              <Link
                to="/admin/dashboard"
                onClick={() => setIsDropdownOpen(false)}
                className="flex items-center px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 mr-3 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Admin Dashboard
              </Link>
            )}
            
            <Link
              to="/affiliate/dashboard"
              onClick={() => setIsDropdownOpen(false)}
              className="flex items-center px-4 py-3 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 mr-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Affiliate Dashboard
            </Link>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Kijelentkezés
            </button>
          </div>
        </div>
      )} 
    </div>
  );
}
